import React, { ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, FormControlLabel, Radio, Snackbar, Typography } from '@mui/material'
import { AxiosError } from 'axios'

import { apiZodios } from '~api/apiZodios'
import {
  AutoReplyOffIcon,
  AutoReplyOnIcon,
  EditIcon,
  PendingIcon,
  VerifiedIcon,
} from '~assets/icons'
import { Button, DeleteButton, EmailEntryBox, SaveCancelButtons } from '~components/common'
import FroalaEditorModal from '~components/common/FroalaEditorModal'
import Table, { TableHeader } from '~components/common/Table'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import { updateUserInfo } from '~redux/slices/authSlice'
import { ContactEmail0, ContactId, HttpStatus, createVerifyStandard } from '~types'

type EmailSettingsTableProps = {
  contactId: ContactId
  onSave?: () => void
  inputEmails?: ContactEmail0[]
  isContactEntry?: boolean
}

const EmailSettingsTable = ({
  contactId,
  onSave,
  inputEmails,
  isContactEntry = false,
}: EmailSettingsTableProps): ReactElement => {
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state?.auth.user)
  const [emails, setEmails] = useState<ContactEmail0[]>(inputEmails || [])
  const [showEmailInput, setShowEmailInput] = useState<boolean>(false)
  const [emailInput, setEmailInput] = useState<string>('')
  const [emailInputValid, setEmailInputValid] = useState<boolean>(false)
  const [emailInUseAlert, setEmailInUseAlert] = React.useState(false)
  const [verificationSentAlert, setVerificationSentAlert] = React.useState(false)

  const fetchEmails = async (): Promise<void> => {
    try {
      if (!user?.id) {
        return
      }
      const response = await apiZodios.contacts_listEmailAddresses({
        user_id: user.id,
        contact_ids: [contactId],
      })
      setEmails(response.contact_emails)
    } catch (_err) {}
  }

  const addEmailAddress = async (): Promise<void> => {
    try {
      if (!user?.id) {
        return
      }
      const newEmailAddress = emailInput
      const tempId = `temp-${Date.now()}`
      const newEmail: ContactEmail0 = {
        id: tempId,
        email: tempId,
        contact: user.id,
        address: newEmailAddress,
        primary: false,
        verified: false,
        ses_verified: false,
        signature: '',
      }
      setEmails((prevEmails) => [...prevEmails, newEmail])
      setShowEmailInput(false)
      setEmailInput('')

      await apiZodios.contacts_createEmailAddress({
        user_id: user?.id,
        address: newEmailAddress,
        verify: true,
        contact_id: contactId,
      })
      fetchEmails()
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === HttpStatus.CONFLICT) {
          setEmailInUseAlert(true)
        }
      }
      fetchEmails()
    }
  }

  const cancelAddEmailAddress = (): void => {
    setShowEmailInput(false)
    setEmailInput('')
  }

  const removeEmailAddress = async (email: ContactEmail0): Promise<void> => {
    try {
      if (!user?.id) {
        return
      }
      setEmails((prevEmails) => prevEmails.filter((e) => e.id !== email.id))

      await apiZodios.contacts_removeEmailAddress({
        user_id: user?.id,
        contact_email_id: email.id,
        contact_id: contactId,
      })
      fetchEmails()
    } catch (_err) {}
  }

  const verifyEmailAddress = async (email: ContactEmail0): Promise<void> => {
    try {
      if (!user?.id) {
        return
      }
      setVerificationSentAlert(true)

      await apiZodios.contacts_verifyEmail({
        user_id: user?.id,
        contact_email_id: email.id,
        verify_options: createVerifyStandard(null),
      })
    } catch (_err) {}
  }

  const setPrimaryEmailAddress = async (email: ContactEmail0): Promise<void> => {
    try {
      if (!user?.id) {
        return
      }
      setEmails((prevEmails) =>
        prevEmails.map((e) => ({
          ...e,
          primary: e.id === email.id,
        })),
      )

      await apiZodios.contacts_setPrimaryEmailAddress({
        user_id: user?.id,
        contact_email_id: email.id,
      })
      fetchEmails()
    } catch (_err) {
      fetchEmails()
    }
  }

  const editEmailSignature = async (email: ContactEmail0, newSignature: string): Promise<void> => {
    try {
      if (!user?.id) {
        return
      }
      setEmails((prevEmails) =>
        prevEmails.map((e) => ({
          ...e,
          signature: e.id === email.id ? newSignature : e.signature,
        })),
      )

      await apiZodios.contacts_setEmailSignature({
        user_id: user?.id,
        contact_email_id: email.id,
        signature: newSignature,
      })
      fetchEmails()
    } catch (_err) {
      fetchEmails()
    }
  }

  const handleEmailAddressChange = (newEmail: string, newIsValid: boolean): void => {
    setEmailInput(newEmail)
    setEmailInputValid(newIsValid)
  }

  const handleCloseAlert = (_event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') {
      return
    }
    setEmailInUseAlert(false)
    setVerificationSentAlert(false)
  }

  useEffect(() => {
    fetchEmails()
  }, [contactId])

  const SetReplyToEmailButtons: React.FC<{ row: ContactEmail0 }> = ({ row }) => {
    const [showSetReplyTo, setShowSetReplyTo] = React.useState(user?.reply_to != row.email)

    const setReplyTo = async (email?: ContactEmail0): Promise<void> => {
      try {
        if (!user?.id) {
          return
        }
        const params: {
          user_id: string
          email_address_id?: string
        } = {
          user_id: user.id,
        }
        if (email) {
          params.email_address_id = email.email
        }
        dispatch(updateUserInfo({ ...user, reply_to: email ? email.email : null }))
        setShowSetReplyTo(!email)

        await apiZodios.contacts_setReplyTo(params)
        const updatedUser = await apiZodios.core_getUser({ user_id: user?.id })
        dispatch(updateUserInfo(updatedUser))
        fetchEmails()
      } catch (_err) {}
    }

    const unsetReplyTo = async (): Promise<void> => {
      setReplyTo()
    }

    return (
      <Box display='flex' style={{ alignItems: 'center' }}>
        <Button
          size='large'
          variant='text'
          onClick={() => (showSetReplyTo ? setReplyTo(row) : unsetReplyTo())}
        >
          {showSetReplyTo ? (
            <AutoReplyOnIcon color={lightTheme.palette.text.primary} />
          ) : (
            <AutoReplyOffIcon color={lightTheme.palette.text.primary} />
          )}
          <Typography variant='body2'>
            {showSetReplyTo ? 'Set as Reply To' : 'Unset as Reply To'}
          </Typography>
        </Button>
      </Box>
    )
  }

  const EmailRowContent: React.FC<{ row: ContactEmail0 }> = ({ row }) => {
    const [showSetReplyTo, _setShowSetReplyTo] = React.useState(user?.reply_to != row.email)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const handleOpenModal = (): void => setIsModalOpen(true)
    const handleCloseModal = (): void => setIsModalOpen(false)

    const handleSaveSignature = (newSignature: string): void => {
      editEmailSignature(row, newSignature)
      handleCloseModal()
    }

    return (
      <Box>
        {isContactEntry ? (
          // ContactEntry [Contact Page]: render subset of email configuration options
          <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
            <FormControlLabel
              control={
                <Radio
                  checked={row.primary}
                  onChange={() => setPrimaryEmailAddress(row)}
                  size='small'
                />
              }
              label={
                <Box className='flex-v-align' gap={2}>
                  <Typography variant='body2'>Primary</Typography>
                  <Typography variant='h4' sx={{ mr: 2, flexShrink: 0 }}>
                    {row.address}
                  </Typography>
                </Box>
              }
              sx={{ marginRight: 2 }}
            />
            <Box>
              <DeleteButton
                item={row}
                onDelete={removeEmailAddress}
                isDisabled={row.primary}
                itemName={row.address}
              />
            </Box>
          </Box>
        ) : (
          // Contact [Account Settings Page]: render all of email configuration options
          <Box>
            <Box display='flex' alignItems='center' mb={1}>
              <Typography variant='h4' sx={{ mr: 2, flexShrink: 0 }}>
                {row.address}
              </Typography>
              <Box display='flex' alignItems='center'>
                {!showSetReplyTo && (
                  <Box mr={2}>
                    <AutoReplyOnIcon color={lightTheme.palette.primary.main} />
                  </Box>
                )}
                <Box mr={2}>{row.verified ? <VerifiedIcon /> : <PendingIcon />}</Box>
                {!row.verified && (
                  <Button
                    variant='text'
                    color='secondary'
                    size='large'
                    onClick={() => {
                      verifyEmailAddress(row)
                    }}
                  >
                    <Typography
                      variant='body2'
                      color='primary.main'
                      sx={{
                        textDecoration: 'underline',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Verify Email
                    </Typography>
                  </Button>
                )}
              </Box>
            </Box>
            <Box
              display='flex'
              alignItems='center'
              sx={{
                width: '100%',
                minHeight: '48px',
                '& > *': {
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <Box display='flex' justifyContent='space-between' width='100%' alignItems='center'>
                <Box display='flex' alignItems='center'>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={row.primary}
                        onChange={() => setPrimaryEmailAddress(row)}
                        size='small'
                      />
                    }
                    label={<Typography variant='body2'>Primary</Typography>}
                    sx={{ marginRight: 2 }}
                  />
                  <SetReplyToEmailButtons row={row} />
                </Box>
                <Box display='flex' alignItems='center'>
                  <Button
                    size='large'
                    onClick={handleOpenModal}
                    sx={{
                      padding: '4px 8px',
                      marginRight: 2,
                    }}
                  >
                    <EditIcon />
                    <Typography variant='body2'>Edit Signature</Typography>
                  </Button>
                  <DeleteButton
                    item={row}
                    onDelete={removeEmailAddress}
                    isDisabled={row.primary}
                    itemName={row.address}
                  />
                </Box>
              </Box>
            </Box>
            <FroalaEditorModal
              open={isModalOpen}
              onClose={handleCloseModal}
              onSave={handleSaveSignature}
              initialContent={row.signature}
              title='Edit Email Signature'
            />
          </Box>
        )}
      </Box>
    )
  }

  const EmailTableHeader: TableHeader<ContactEmail0>[] = [
    {
      id: 'address',
      label: 'Email Information',
      render: (row) => <EmailRowContent row={row} />,
    },
  ]

  return (
    <Box display='flex' flexDirection='column' width='100%' maxWidth='600px' padding={2}>
      <Table<ContactEmail0>
        headers={EmailTableHeader}
        data={emails}
        loading={false}
        ignoreMaxHeight={true}
      />
      {showEmailInput ? (
        <Box style={{ margin: lightTheme.spacing(2) }}>
          <EmailEntryBox inputEmailAddress={emailInput} onChange={handleEmailAddressChange} />
          <SaveCancelButtons
            onSave={addEmailAddress}
            onCancel={cancelAddEmailAddress}
            isSaveDisabled={!emailInputValid}
          ></SaveCancelButtons>
        </Box>
      ) : (
        <Box display='flex' justifyContent='flex-end'>
          <Button
            variant='outlined'
            color='secondary'
            style={{ margin: lightTheme.spacing(2) }}
            onClick={() => setShowEmailInput(true)}
          >
            Add Email
          </Button>
          {onSave && (
            <Button
              variant='contained'
              color='secondary'
              style={{ margin: lightTheme.spacing(2) }}
              onClick={onSave}
            >
              Save
            </Button>
          )}
        </Box>
      )}
      <Snackbar open={emailInUseAlert} autoHideDuration={5000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity='error' sx={{ width: '100%' }}>
          Email already in use.
        </Alert>
      </Snackbar>
      <Snackbar open={verificationSentAlert} autoHideDuration={5000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity='info' sx={{ width: '100%' }}>
          Verification email sent.
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default EmailSettingsTable
